import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { formatEuro } from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import { Button, useToast } from '../../../ds/components';
import { errorMessage } from '../../../lib/api';
import { suggestUnit, type FinancierRecordFields, type OffersView } from './api';
import styles from './financier.module.css';

/** Sidebar cards of the financier screens (Finanzierer 03–11): eyebrow + content, 20/22 padding. */

export function SideCard({
  label,
  children,
  className,
}: {
  label: string;
  children?: ReactNode;
  className?: string | undefined;
}) {
  return (
    <section className={clsx(styles.card, className)} aria-label={label}>
      <div className={styles.eyebrow}>{label}</div>
      {children}
    </section>
  );
}

function euroOrDash(cents: number | null): string {
  return cents === null ? '–' : formatEuro(cents);
}

/** „Kundenprofil" (FZ-03/07): the latest signed self-disclosure in four rows + „Selbstauskunft öffnen". */
export function CustomerProfileCard({ financing }: { financing: FinancierRecordFields }) {
  const navigate = useNavigate();
  const profile = financing.customerProfile;
  if (!profile) {
    return (
      <SideCard label="Kundenprofil">
        <div className={styles.sideText}>Noch keine unterschriebene Selbstauskunft.</div>
      </SideCard>
    );
  }
  const rows: Array<[string, string]> = [
    ['Beschäftigung', profile.employment ?? '–'],
    [
      'Haushaltsnetto',
      profile.householdNetCents === null ? '–' : `${formatEuro(profile.householdNetCents)} / Monat`,
    ],
    ['Eigenkapital', euroOrDash(profile.equityCents)],
    [
      'Volumen',
      profile.volumeCents === null
        ? '–'
        : `${formatEuro(profile.volumeCents)}${profile.volumeTag ? ` · ${profile.volumeTag}` : ''}`,
    ],
  ];
  return (
    <SideCard label="Kundenprofil">
      <dl className={styles.kvList}>
        {rows.map(([label, value]) => (
          <div key={label} className={styles.kv}>
            <dt className={styles.kvLabel}>{label}</dt>
            <dd className={styles.kvValue}>{value}</dd>
          </div>
        ))}
      </dl>
      <div style={{ marginTop: 12 }}>
        <button
          type="button"
          className={styles.link}
          onClick={() => void navigate(profile.selfDisclosureHref)}
        >
          Selbstauskunft öffnen
        </button>
      </div>
    </SideCard>
  );
}

/** „Berater" (FZ-03/07/08): avatar, name, line; „Nachricht" → mailto, „Anrufen" → tel (no internal messaging exists). */
export function AdvisorCard({
  financing,
  withActions = true,
}: {
  financing: FinancierRecordFields;
  withActions?: boolean;
}) {
  const advisor = financing.advisor;
  if (!advisor) return null;
  return (
    <SideCard label="Berater">
      <div className={styles.person}>
        <span className={styles.avatar} aria-hidden="true">
          {advisor.initials}
        </span>
        <div>
          <div className={styles.personName}>{advisor.name}</div>
          <div className={styles.personLine}>{advisor.line}</div>
        </div>
      </div>
      {withActions ? (
        <div className={styles.personActions}>
          <a className={clsx('pressable', styles.smallButton)} href={`mailto:${advisor.email}`}>
            Nachricht
          </a>
          {advisor.phone ? (
            <a
              className={clsx('pressable', styles.smallButton)}
              href={`tel:${advisor.phone.replace(/\s+/g, '')}`}
            >
              Anrufen
            </a>
          ) : null}
        </div>
      ) : null}
      {advisor.hint ? <div className={styles.personHint}>{advisor.hint}</div> : null}
    </SideCard>
  );
}

/** „Anfrage" (FZ-06/10): the request note line of the offers view + „Vermerk bearbeiten". */
export function RequestSideCard({ text, onEdit }: { text: string; onEdit?: (() => void) | undefined }) {
  return (
    <SideCard label="Anfrage">
      <div className={styles.sideText}>{text}</div>
      {onEdit ? (
        <div style={{ marginTop: 12 }}>
          <button type="button" className={styles.linkMuted} onClick={onEdit}>
            Vermerk bearbeiten
          </button>
        </div>
      ) : null}
    </SideCard>
  );
}

/** „Wählen" (FZ-06). */
export function ChooseRuleCard() {
  return (
    <SideCard label="Wählen">
      <div className={styles.sideText}>
        Sie wählen das Angebot allein. Das gewählte Angebot wird zur Zusage und bestimmt die Stufe.
      </div>
    </SideCard>
  );
}

/** „Hinweis" (FZ-10). */
export function UploadHintCard() {
  return (
    <SideCard label="Hinweis">
      <div className={styles.sideText}>
        Angenommen werden PDF-Dateien bis 20 MB. Gescannte Angebote lassen sich hochladen, die Eckdaten tragen
        Sie dann selbst ein.
      </div>
    </SideCard>
  );
}

/** „Regel" (FZ-11). */
export function ExpiryRuleCard() {
  return (
    <SideCard label="Regel">
      <div className={styles.sideText}>
        Drei Tage vor Ablauf des gewählten Angebots entsteht eine Task. Abgelaufene Angebote bleiben sichtbar,
        lassen sich aber nicht wählen.
      </div>
    </SideCard>
  );
}

/** „Offene Tasks" (FZ-06/11): the record's open tasks of the financing, its contact and deal. */
export function OpenTasksCard({ financing }: { financing: FinancierRecordFields }) {
  return (
    <SideCard label="Offene Tasks">
      {financing.openTasks.length === 0 ? (
        <div className={styles.sideText}>Keine offenen Tasks.</div>
      ) : (
        <ul className={styles.taskList}>
          {financing.openTasks.map((task) => (
            <li key={task.id} className={styles.taskRow}>
              <span className={styles.taskBox} aria-hidden="true" />
              <span className={styles.taskText}>
                <span className={styles.taskTitle}>{task.title}</span>
                <span className={styles.taskContext}>{task.contextLine}</span>
              </span>
              <span className={styles.taskDue}>{task.dueLabel}</span>
            </li>
          ))}
        </ul>
      )}
    </SideCard>
  );
}

/** „Nächster Schritt" (FZ-05) next to „Unterlagen vollständig" (the frame leaves the card empty). */
export function NextStepSideCard() {
  return (
    <SideCard label="Nächster Schritt">
      <div className={styles.sideText}>Extern anfragen und „Als angefragt vermerken“.</div>
    </SideCard>
  );
}

/** „Einheit wechseln?" (FZ-08): the cheaper free unit + „Berater vorschlagen" (`POST …/suggest-unit`, toast). */
export function UnitSuggestionCard({
  financingId,
  suggestion,
  canEdit,
}: {
  financingId: string;
  suggestion: NonNullable<OffersView['unitSuggestion']>;
  canEdit: boolean;
}) {
  const { toast } = useToast();
  const [pending, setPending] = useState(false);
  const [sent, setSent] = useState(false);
  async function propose() {
    setPending(true);
    try {
      await suggestUnit(financingId, suggestion.unitId);
      setSent(true);
      toast({
        message: suggestion.advisorName
          ? `Vorschlag an ${suggestion.advisorName} gesendet.`
          : 'Vorschlag an den Berater gesendet.',
      });
    } catch (error) {
      toast({ message: errorMessage(error) });
    } finally {
      setPending(false);
    }
  }
  return (
    <SideCard label="Einheit wechseln?">
      <div className={styles.sideText} style={{ marginTop: 12, lineHeight: 1.5 }}>
        Vor dem Verlust kann der Berater eine günstigere Einheit anbieten. {suggestion.text}
      </div>
      {canEdit ? (
        <div className={styles.sideActions}>
          <Button
            variant="secondary"
            size="small"
            loading={pending}
            disabled={sent}
            onClick={() => void propose()}
          >
            Berater vorschlagen
          </Button>
        </div>
      ) : null}
    </SideCard>
  );
}
